/**
 * File: siteTutorialsData.ts
 * Path: apps/admin/src/shared/utils/siteTutorialsData.ts
 * Description: The tutorials for the rest of the admin: the website content
 * (Portfolio, Academy, Services, Reviews, Search & AI), the tools, referrals,
 * quotes, notifications, diagnostics, settings, the section menus and the
 * website analytics. Button names are the live ones.
 */

import { ADMIN_PATHS } from "@idk/config";
import { HEAD, PAGE_ACTIONS, type Tutorial } from "./tutorialTypes";

const P = ADMIN_PATHS;

export const SITE_TUTORIALS: Tutorial[] = [
  {
    id: "analytics-read",
    title: "Read your website traffic",
    hint: "2 steps",
    pages: [P.dashboard],
    group: "Website: Analytics",
    steps: [
      {
        target: PAGE_ACTIONS,
        title: "Dates",
        text: "Pick the dates to look at. The numbers and the chart below follow them.",
      },
      {
        target: HEAD,
        title: "Website Analytics",
        text: "This page shows how many people visit the website and what they do.",
      },
    ],
  },
  {
    id: "portfolio-add",
    title: "Add a case study to the portfolio",
    hint: "2 steps",
    pages: [P.portfolio.root],
    group: "Website: Portfolio",
    steps: [
      {
        target: PAGE_ACTIONS,
        title: "Create Entry",
        text: "Press Create Entry to start a new case study. Fill in the client, what you did and the pictures, then press Save Case Study.",
      },
      {
        target: "main table",
        title: "Your case studies",
        text: "Each row is one case study. Open its menu to edit, preview or delete it. A change reaches the public site at the next deploy.",
      },
    ],
  },
  {
    id: "portfolio-edit",
    title: "Edit a case study",
    hint: "2 steps",
    pages: [P.portfolio.edit(":id"), P.portfolio.new],
    group: "Website: Portfolio",
    steps: [
      {
        target: HEAD,
        title: "The editor",
        text: "The tabs hold the general details, the hero picture, the numbers, the credits and the assets. Work through them one at a time.",
      },
      {
        target: PAGE_ACTIONS,
        title: "Save",
        text: "Press Save Case Study when you are done. The change reaches the public website at the next deploy.",
      },
    ],
  },
  {
    id: "academy-add",
    title: "Write an Academy article",
    hint: "2 steps",
    pages: [P.academy.root],
    group: "Website: Academy",
    steps: [
      {
        target: PAGE_ACTIONS,
        title: "Draft Article",
        text: "Press Draft Article to start a new post. Add the title, the picture and the words, then save it.",
      },
      {
        target: "main table",
        title: "Your articles",
        text: "Each row is one article. Open its menu to edit, preview or delete it.",
      },
    ],
  },
  {
    id: "academy-edit",
    title: "Edit an article",
    hint: "1 step",
    pages: [P.academy.edit(":id"), P.academy.new],
    group: "Website: Academy",
    steps: [
      {
        target: HEAD,
        title: "The editor",
        text: "Fill in the identity, the hero picture and the words in each section. Preview it before you save.",
      },
    ],
  },
  {
    id: "services-edit",
    title: "Change the wording of a service",
    hint: "2 steps",
    pages: [P.services.root],
    group: "Website: Services",
    steps: [
      {
        target: HEAD,
        title: "The list",
        text: "These are the services on the website, in the order it lists them. Prices are set in code, so only the wording is edited here.",
      },
      {
        target: "main",
        title: "Open one",
        text: "Press a service to edit its wording, features and questions. The change reaches the public site at the next deploy.",
      },
    ],
  },
  {
    id: "service-detail",
    title: "Edit one service",
    hint: "1 step",
    pages: [P.services.detail(":id")],
    group: "Website: Services",
    steps: [
      {
        target: HEAD,
        title: "The service",
        text: "Change its wording, features and questions. The price is shown but not edited here.",
      },
    ],
  },
  {
    id: "reviews-link",
    title: "Ask a client for a Google review",
    hint: "2 steps",
    pages: [P.reviews],
    group: "Website: Reviews",
    steps: [
      {
        target: "main section",
        title: "Your review link",
        text: "Copy the Google review link here and send it to a happy client.",
      },
      {
        target: HEAD,
        title: "Show real reviews",
        text: "The cards below send review requests, and show real reviews on the case study they are about.",
      },
    ],
  },
  {
    id: "seo-weakest",
    title: "Find the page that needs work for search",
    hint: "2 steps",
    pages: [P.seo],
    group: "Website: Search & AI",
    steps: [
      {
        target: HEAD,
        title: "Weakest first",
        text: "Every case study and article is scored on how ready it is for Google and AI answers. The weakest is first.",
      },
      {
        target: "main",
        title: "Open one",
        text: "Press a row to see its checks and what to fix. Fix them in the case study or article itself.",
      },
    ],
  },
  {
    id: "tools-audit",
    title: "Audit any website",
    hint: "2 steps",
    pages: [P.tools.audit],
    group: "Tools",
    steps: [
      {
        target: "main form, main section",
        title: "The website",
        text: "Type the website address and choose how to send the result. To audit a lead, use the Website audit tab on their page instead.",
      },
      {
        target: HEAD,
        title: "Run it",
        text: "Press Run audit and download to get the PDF report. The email option sends it for you.",
      },
    ],
  },
  {
    id: "tools-brand",
    title: "Build a brand profile",
    hint: "1 step",
    pages: [P.tools.brand],
    group: "Tools",
    steps: [
      {
        target: HEAD,
        title: "Brand Kit",
        text: "Answer the alignment questions, or pick a corporate or creative preset for a fast profile. The result can be downloaded.",
      },
    ],
  },
  {
    id: "referrals-manage",
    title: "Manage referral partners",
    hint: "1 step",
    pages: [P.referrals.root],
    group: "CRM: Referrals",
    steps: [
      {
        target: HEAD,
        title: "Referrals",
        text: "Your affiliates, their commission per service, and each referral. Edit or delete a referral from its row.",
      },
    ],
  },
  {
    id: "quote-new",
    title: "Make a quote",
    hint: "2 steps",
    pages: [P.quotes.new],
    group: "CRM: Quotes",
    steps: [
      {
        target: '[aria-label="Quote lines"]',
        title: "Quote lines",
        text: "Pick what they are getting from your price list. Set how many and the price for each line.",
      },
      {
        target: '[aria-label="Quote total"]',
        title: "The total",
        text: "The total is worked out for you. Check the preview, then make the PDF.",
      },
    ],
  },
  {
    id: "quote-estimate",
    title: "Send an estimate to someone new",
    hint: "1 step",
    pages: [P.quotes.estimate],
    group: "CRM: Quotes",
    steps: [
      {
        target: HEAD,
        title: "Estimate",
        text: "For someone who asked for a price before you met. Answer the questions for them and they get a PDF called an estimate.",
      },
    ],
  },
  {
    id: "notifications-read",
    title: "Read and clear your notifications",
    hint: "2 steps",
    pages: [P.notifications],
    group: "System",
    steps: [
      {
        target: '[aria-label="Notifications"]',
        title: "The list",
        text: "Every notification sent to you. Press one to open the page it is about.",
      },
      {
        target: PAGE_ACTIONS,
        title: "Mark all read",
        text: "Clears the unread count in one press. Nothing is deleted.",
      },
    ],
  },
  {
    id: "diagnostics-check",
    title: "Check that notifications are working",
    hint: "2 steps",
    pages: [P.diagnostics],
    group: "System",
    steps: [
      {
        target: PAGE_ACTIONS,
        title: "Run again and copy",
        text: "Run the checks again after you fix something. Copy the result to send it to whoever helps you.",
      },
      {
        target: HEAD,
        title: "What each check says",
        text: "Each check says if something works, and what to do if it does not.",
      },
    ],
  },
  {
    id: "settings-profile",
    title: "Change your profile and how the admin looks",
    hint: "1 step",
    pages: [P.settings],
    group: "System",
    steps: [
      {
        target: HEAD,
        title: "Settings",
        text: "Your profile, how the admin looks, and the company's details. Some tabs show only for roles that may use them.",
      },
    ],
  },
  {
    id: "menu-find",
    title: "Find a page",
    hint: "1 step",
    pages: [P.menu.crm, P.menu.accounting, P.menu.cms],
    group: "System",
    steps: [
      {
        target: "main nav",
        title: "This section",
        text: "Every page in this section is listed here with one line on what it does. Press one to open it.",
      },
    ],
  },
  {
    id: "howto-find",
    title: "Find a tutorial for any job",
    hint: "1 step",
    pages: [P.accounting.guide],
    group: "System",
    steps: [
      {
        target: HEAD,
        title: "How to",
        text: "Every tutorial in the admin is listed here by section. Press Show me and the admin opens the page and lights up each thing to press.",
      },
    ],
  },
  {
    id: "numbers-glossary",
    title: "Look up what a number means",
    hint: "1 step",
    pages: [P.numbers.glossary],
    group: "System",
    steps: [
      {
        target: HEAD,
        title: "Glossary",
        text: "Each number on the dashboard is explained here in plain words.",
      },
    ],
  },
];
